/**
 * Utility functions for handling Stripe minimum payment requirements
 */

export const STRIPE_MINIMUM_CENTS = 2000; // £20 minimum

/**
 * Calculate the minimum quantity required to meet Stripe's minimum payment requirement
 * @param pricePerUnit - Price per unit in pounds (e.g., 0.00001)
 * @returns Minimum quantity needed to reach £20
 */
export function calculateMinimumQuantityForStripe(pricePerUnit: number): number {
  if (pricePerUnit <= 0) return 1;

  // Don't round the price here - use exact decimal for calculation
  const priceInCents = pricePerUnit * 100;
  return Math.ceil(STRIPE_MINIMUM_CENTS / priceInCents);
}

/**
 * Calculate the effective minimum quantity considering both scraper minimum and Stripe minimum
 * @param pricePerUnit - Price per unit in pounds
 * @param scraperMinQuantity - Minimum quantity set by the scraper owner
 * @returns The higher of the two minimums
 */
export function calculateEffectiveMinimumQuantity(
  pricePerUnit: number,
  scraperMinQuantity: number = 1,
): number {
  const stripeMinQuantity = calculateMinimumQuantityForStripe(pricePerUnit);
  return Math.max(scraperMinQuantity, stripeMinQuantity);
}

/**
 * Validate that a quantity and price combination meets Stripe's minimum
 * @param pricePerUnit - Price per unit in pounds
 * @param quantity - Quantity to validate
 * @returns Object with validation result and error message if applicable
 */
export function validateStripeMinimum(
  pricePerUnit: number,
  quantity: number,
): { isValid: boolean; errorMessage?: string; requiredQuantity?: number } {
  const totalInCents = pricePerUnit * quantity * 100;

  if (totalInCents >= STRIPE_MINIMUM_CENTS) {
    return { isValid: true };
  }

  const requiredQuantity = calculateMinimumQuantityForStripe(pricePerUnit);

  return {
    isValid: false,
    errorMessage: `Due to payment processing requirements, minimum order is ${requiredQuantity} leads (£20 minimum total).`,
    requiredQuantity,
  };
}

/**
 * Get explanation text for why a minimum quantity is required
 * @param pricePerUnit - Price per unit in pounds
 * @param scraperMinQuantity - Minimum quantity set by the scraper owner
 * @returns Explanation text or null if no special minimum is needed
 */
export function getMinimumQuantityExplanation(
  pricePerUnit: number,
  scraperMinQuantity: number = 1,
): string | null {
  const stripeMinQuantity = calculateMinimumQuantityForStripe(pricePerUnit);

  if (stripeMinQuantity > scraperMinQuantity) {
    return `Due to payment processing requirements, minimum order quantity must be at least ${stripeMinQuantity} leads (£20 minimum total).`;
  }

  return null;
}
